import Button from '@atoms/Button/Button';
import Icon from '@atoms/Icon/Icon';
import Image from '@atoms/Image/Image';
import PriceDisplayText from '@atoms/PriceDisplayText/PriceDisplayText';
import ReturnLink from '@atoms/ReturnLink/ReturnLink';
import ShadowContainerHorizontal, {
  IShadowContainerHorizontalRef,
} from '@atoms/ShadowContainerHorizontal/ShadowContainerHorizontal';
import ShadowContainerVertical from '@atoms/ShadowContainerVertical/ShadowContainerVertical';
import { IAnchorLink } from '@models/IAnchorLink';
import { ICTALinks } from '@models/ICTALinks';
import { IImage } from '@models/IImage';
import { ILink } from '@models/ILink';
import { IPricingData } from '@models/IPricingData';
import BreakpointTypeEnum from '@models/enums/BreakpointTypeEnum';
import BackgroundColourEnum from '@models/enums/IBackgroundColourEnum';
import StickyNavPosition from '@models/enums/StickyNavPositionEnum';
import useComponentsHeightsStore from '@state/ComponentsHeightsStore';
import useStickyNavPositionStore from '@state/StickyNavStore';
import updateDataLayer from '@utils/helpers/gtm';
import useAnchorNavScrolling from '@utils/hooks/useAnchorNavScrolling';
import useAriaLabels from '@utils/hooks/useAriaLabels';
import useMediaQuery from '@utils/hooks/useMediaQuery';
import useResizeObserver from '@utils/hooks/useResizeObserver';
import classNames from 'classnames';
import { AnimatePresence, motion } from 'framer-motion';
import { useEffect, useRef, useState } from 'react';

interface Props {
  readonly title?: string;
  readonly subTitle?: string;
  readonly vatLabel?: string;
  readonly shippingLabel?: string;
  readonly thumbnail?: IImage;
  readonly icon?: IImage;
  readonly buyNowLink?: ILink;
  readonly anchorNavigationItems: IAnchorLink[];
  readonly ctaLinks?: ICTALinks;
  readonly pricingData?: IPricingData;
  readonly fromPriceLabel?: string;
  readonly navPosition?: StickyNavPosition.Top | StickyNavPosition.Bottom;
  readonly returnLink?: ILink;
  readonly backgroundColour?:
    | BackgroundColourEnum.Black
    | BackgroundColourEnum.Grey;
  readonly showReturnLinkMobileOnly?: boolean;
  readonly shouldHavePadding?: boolean;
}

const StickyNavigation = ({
  title,
  subTitle,
  vatLabel,
  shippingLabel,
  thumbnail,
  icon,
  buyNowLink,
  anchorNavigationItems,
  ctaLinks,
  pricingData,
  fromPriceLabel,
  navPosition = StickyNavPosition.Top,
  returnLink,
  backgroundColour = BackgroundColourEnum.Black,
  showReturnLinkMobileOnly,
  shouldHavePadding,
}: Props) => {
  const [isNavItemsDropdownDisplayed, setIsNavItemsDropdownDisplayed] =
    useState(false);
  const [stickToBottom, setStickToBottom] = useState(false);
  const isMobile = useMediaQuery(1024);
  const isMobileScreen = useMediaQuery(1024, BreakpointTypeEnum.Screen);
  const isZoomedDesktop = !isMobileScreen && isMobile;
  const isBelowLargeDesktop = useMediaQuery(1440);
  const [containerRefCallback, { clientHeight: stickyNavHeight }] =
    useResizeObserver();
  const navRef = useRef<HTMLDivElement | null>(null);
  const tabbedNavRef = useRef<HTMLDivElement | null>(null);
  const containerRef = useRef<IShadowContainerHorizontalRef>(null);
  const [currentBackgroundColour, setCurrentBackgroundColour] =
    useState(backgroundColour);
  const { setStickyNavPosition } = useStickyNavPositionStore((state) => ({
    setStickyNavPosition: state.setStickyNavPosition,
  }));
  const { setStickyNavigationTopHeight, setStickyNavigationBottomHeight } =
    useComponentsHeightsStore((state) => ({
      setStickyNavigationTopHeight: state.setStickyNavigationTopHeight,
      setStickyNavigationBottomHeight: state.setStickyNavigationBottomHeight,
    }));
  const { closeModalAriaLabel, openMenuAriaLabel } = useAriaLabels();
  const { activeAnchorId, scrollTo } = useAnchorNavScrolling({
    items: anchorNavigationItems,
    stickToBottom,
  });
  const colourCodesMapper = {
    [BackgroundColourEnum.Grey]: '#1F191A',
    [BackgroundColourEnum.Black]: '#0A0203',
  };

  useEffect(() => {
    setStickyNavigationTopHeight(stickToBottom ? 0 : stickyNavHeight);
    setStickyNavigationBottomHeight(stickToBottom ? stickyNavHeight : 0);
  }, [
    stickToBottom,
    stickyNavHeight,
    setStickyNavigationTopHeight,
    setStickyNavigationBottomHeight,
  ]);

  useEffect(() => {
    if (!isBelowLargeDesktop) {
      setIsNavItemsDropdownDisplayed(false);
    }
  }, [isBelowLargeDesktop]);

  const ctaNavItems = ctaLinks
    ? []
    : anchorNavigationItems
        .filter((item) => item.primary || item.secondary)
        .slice(0, 2);

  const nonCtaNavItems = ctaLinks
    ? anchorNavigationItems.map((item) => ({
        ...item,
        primary: false,
        secondary: false,
      }))
    : anchorNavigationItems.filter((item) => !item.primary && !item.secondary);

  const mobileDropdownNavItems = [
    ...nonCtaNavItems,
    ...(ctaNavItems.length === 2 ? ctaNavItems : []),
  ];

  const displayedCTALinks =
    ctaLinks?.links
      .filter((ctaLink) => ctaLink.link.url && ctaLink.link.name)
      .slice(0, 2) ?? [];

  const closeMobileNavDropdown = () => {
    setTimeout(() => setIsNavItemsDropdownDisplayed(false), 750);
  };

  useEffect(() => {
    if (!navRef?.current) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        const newBackgroundColour =
          entry?.intersectionRatio === 1
            ? backgroundColour
            : BackgroundColourEnum.Black;
        setCurrentBackgroundColour(newBackgroundColour);
      },
      { threshold: 1 }
    );

    observer.observe(navRef.current);
  }, [backgroundColour]);

  useEffect(() => {
    if (!stickToBottom || isZoomedDesktop) return;

    const productNav = document.getElementById('ProductNavigation');
    if (!productNav) return;

    if (isMobile) {
      document.documentElement.style.setProperty(
        'padding-bottom',
        `${stickyNavHeight - 1}px`
        // Used that "- 1" to cover the sub-pixel rendering issue noticed in some browsers at certain zoom levels
      );
      productNav.classList.remove('sticky', 'top-0');
      productNav.classList.add('fixed', 'bottom-0', 'w-full');
      productNav.style.top = 'auto';
    } else {
      document.documentElement.style.removeProperty('padding-bottom');

      if (productNav.style.top === 'auto') productNav.style.top = '';
      productNav.classList.add('sticky', 'top-0');
      productNav.classList.remove('fixed', 'bottom-0', 'w-full');
    }
  }, [stickToBottom, isMobile, stickyNavHeight, isZoomedDesktop]);

  useEffect(() => {
    setStickToBottom(
      navPosition === StickyNavPosition.Bottom && !!isMobile && !isZoomedDesktop
    );
    setStickyNavPosition(navPosition);
  }, [navPosition, setStickyNavPosition, isMobile, isZoomedDesktop]);

  useEffect(() => {
    if (!containerRef.current || !tabbedNavRef.current) return;

    const activeNavigationElement = tabbedNavRef.current?.querySelector(
      'a[data-nav-active="true"]'
    ) as HTMLElement;

    if (activeNavigationElement) {
      const containerWidth = containerRef.current.getOffsetWidth();
      const leftOffset =
        activeNavigationElement.offsetLeft +
        activeNavigationElement.clientWidth / 2 -
        containerWidth / 2;

      containerRef.current.scrollTo(leftOffset);
    }
  }, []);

  return (
    <motion.div
      ref={(node) => {
        navRef.current = node;
        containerRefCallback(node);
      }}
      initial={{
        backgroundColor: colourCodesMapper[backgroundColour],
      }}
      animate={{
        backgroundColor: colourCodesMapper[currentBackgroundColour],
      }}
      className={classNames(
        {
          'pt-20': anchorNavigationItems.length === 0,
        },
        'dark relative z-10 flex min-h-[76px] flex-row items-center px-5 py-7.5 xl:px-7.5 xl:py-6',
        {
          'mb-7.5 lg:mb-24': shouldHavePadding,
        }
      )}
      data-testid="CT-06-Sticky-Navigation"
    >
      {returnLink?.name && returnLink?.url && (
        <div
          className={classNames(
            'pointer-events-none absolute left-0 flex px-5 lg:px-7',
            {
              'xl:hidden': showReturnLinkMobileOnly,
            }
          )}
        >
          <span className="pointer-events-auto">
            <ReturnLink
              link={returnLink}
              dataTestId="CT-06-Sticky-Navigation-Return-Link"
            />
          </span>
        </div>
      )}

      {buyNowLink?.name && (
        <div className="flex w-1/2 flex-col items-start text-white md:flex-row md:items-center xl:w-1/3">
          {thumbnail && (
            <Image
              image={thumbnail}
              imageClasses="md:mr-7.5 w-20 object-contain hidden md:block"
              sizes="300px"
              dataTestId="CT-06-Sticky-Navigation-Thumbnail"
            />
          )}

          <div className="md:pr-7.5">
            {icon ? (
              <Image
                image={icon}
                imageClasses="w-7.5 mb-2"
                sizes="300px"
                dataTestId="CT-06-Sticky-Navigation-Icon"
              />
            ) : (
              <p
                className="body-two"
                data-testid="CT-06-Sticky-Navigation-Title"
              >
                {title}
              </p>
            )}

            {subTitle && (
              <p
                className="body-four hidden md:block"
                data-testid="CT-06-Sticky-Navigation-Subtitle"
              >
                {subTitle}
              </p>
            )}
          </div>

          {pricingData?.formattedCalculatedPrice && (
            <div className="mt-2 border-white md:mt-0 md:border-l md:pl-7.5">
              <PriceDisplayText
                pricingData={pricingData}
                vatLabel={vatLabel}
                shippingLabel={shippingLabel}
                fromPriceLabel={fromPriceLabel}
                smallText
                dataTestId="CT-06-Sticky-Navigation-Price-Display-Text"
              />
            </div>
          )}
        </div>
      )}

      <ul
        className="ml-auto flex h-full w-1/2 select-none items-center justify-end xl:w-2/3"
        data-testid="CT-06-Sticky-Navigation-Nav-Item-Container"
      >
        <motion.li
          className="flex h-full overflow-hidden"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
        >
          <ShadowContainerHorizontal
            shadowColour={
              backgroundColour === BackgroundColourEnum.Black
                ? 'from-black-100'
                : 'from-grey-600'
            }
            className="flex w-full justify-end"
            threshold={0.01}
            hasTabbableNestedItems
          >
            <div className="flex">
              {nonCtaNavItems.map((item, index) => (
                <div
                  key={item.anchor}
                  className="hidden shrink-0 text-white xl:block"
                  data-testid={`CT-06-Sticky-Navigation-Non-CTA-Nav-Item-${index}`}
                >
                  <button
                    type="button"
                    onClick={() => {
                      scrollTo(`a-${item.anchor}`);

                      if (pricingData?.formattedCalculatedPrice) {
                        updateDataLayer({
                          event: 'menuUsed',
                          content_type: 'pdp_menu',
                          main_category: item.title,
                        });
                      } else {
                        updateDataLayer({
                          event: 'menuUsed',
                          content_type: 'anchor_menu',
                          main_category: item.title,
                        });
                      }
                    }}
                    className={classNames(
                      {
                        'border-yellow border-b text-yellow':
                          item.anchor === activeAnchorId,
                      },
                      'body-two ml-7.5 transition-all duration-100 hover:text-grey-500'
                    )}
                    data-testid={`CT-06-Sticky-Navigation-Non-CTA-Nav-Item-${index}-Button`}
                  >
                    {item.title}
                  </button>
                </div>
              ))}
            </div>
          </ShadowContainerHorizontal>
        </motion.li>

        {(mobileDropdownNavItems.length > 0 ||
          displayedCTALinks.length === 2) && (
          <li className="flex items-center xl:hidden">
            <div className="mr-5 flex h-9 w-9 items-center justify-center rounded-full border border-yellow bg-grey-600">
              <motion.button
                animate={{
                  rotate: isNavItemsDropdownDisplayed ? 180 : 0,
                }}
                type="button"
                onClick={() =>
                  setIsNavItemsDropdownDisplayed(!isNavItemsDropdownDisplayed)
                }
                aria-label={
                  isNavItemsDropdownDisplayed
                    ? closeModalAriaLabel
                    : openMenuAriaLabel
                }
                data-testid="CT-06-Sticky-Navigation-Dropdown-Button"
              >
                <Icon
                  name="caret_down"
                  colorClassName="text-yellow"
                  className={stickToBottom ? 'rotate-180' : ''}
                />
              </motion.button>
            </div>
          </li>
        )}

        {ctaNavItems.map((item, index) => (
          <li
            className={classNames('ml-7.5 shrink-0', {
              'hidden xl:block': ctaNavItems.length === 2,
            })}
            key={item.anchor}
            data-testid={`CT-06-Sticky-Navigation-CTA-Nav-Item-${index}`}
          >
            <Button
              className="min-h-min"
              onClick={() => scrollTo(`a-${item.anchor}`)}
              secondary={!item.primary && item.secondary}
              linkCallback={() => {
                updateDataLayer({
                  event: 'menuUsed',
                  content_type: 'anchor_menu',
                  main_cta: item.title,
                });
              }}
              dataTestId={`CT-06-Sticky-Navigation-CTA-Nav-Item-${index}-Button`}
            >
              {item.title}
            </Button>
          </li>
        ))}

        {displayedCTALinks.map((ctaLink, index) => (
          <li
            className={classNames('ml-7.5 shrink-0', {
              'hidden xl:block': displayedCTALinks.length === 2,
            })}
            key={`${ctaLink.link.name} - ${ctaLink.link.url}`}
            data-testid={`CT-06-Sticky-Navigation-Displayed-CTA-Link-${index}`}
          >
            <Button
              className="min-h-min"
              link={ctaLink.link}
              secondary={ctaLink.secondary}
              linkCallback={() => {
                updateDataLayer({
                  event: 'menuUsed',
                  content_type: 'anchor_menu',
                  main_cta: ctaLink.link.name,
                });
              }}
              data-testid={`CT-06-Sticky-Navigation-Displayed-CTA-Link-${index}-Button`}
            />
          </li>
        ))}

        {buyNowLink?.name && buyNowLink.url && (
          <li className="flex items-center">
            <Button
              link={buyNowLink}
              linkCallback={() => {
                updateDataLayer({
                  event: 'menuUsed',
                  content_type: 'pdp_menu',
                  main_cta: buyNowLink.name,
                });
              }}
              dataTestId="CT-06-Sticky-Navigation-Buy-Now-Link"
              className="text-center lg:ml-5 xl:ml-7.5"
            />
          </li>
        )}
      </ul>

      <AnimatePresence>
        {isNavItemsDropdownDisplayed && (
          <motion.div
            key="menu"
            initial={{ translateY: stickToBottom ? 20 : -20, opacity: 0 }}
            animate={{ translateY: 0, opacity: 1 }}
            exit={{ translateY: stickToBottom ? 20 : -20, opacity: 0 }}
            transition={{
              duration: 0.5,
              ease: [0.74, 0, 0.19, 1.02],
            }}
            className={classNames(
              stickToBottom ? 'bottom-full' : 'top-full',
              'absolute left-0 z-0 w-full'
            )}
            data-testid="CT-06-Sticky-Navigation-Dropdown"
          >
            <ShadowContainerVertical
              className="flex max-h-60 flex-col gap-y-7.5 bg-white shadow-lg sm:max-h-80"
              shadowColour="from-white"
              shadowHeight="h-16 sm:h-20"
            >
              <ul className="flex flex-col gap-y-7.5 px-5 py-7.5">
                {mobileDropdownNavItems.map((item, index) => (
                  <li key={item.anchor}>
                    {!item.primary && !item.secondary ? (
                      item.title && (
                        <button
                          type="button"
                          onClick={() => {
                            scrollTo(`a-${item.anchor}`);
                            closeMobileNavDropdown();

                            if (pricingData?.formattedCalculatedPrice) {
                              updateDataLayer({
                                event: 'menuUsed',
                                content_type: 'pdp_menu',
                                main_category: item.title,
                              });
                            } else {
                              updateDataLayer({
                                event: 'menuUsed',
                                content_type: 'anchor_menu',
                                main_category: item.title,
                              });
                            }
                          }}
                          onKeyDown={() => {
                            scrollTo(`a-${item.anchor}`);
                            closeMobileNavDropdown();
                          }}
                          className={classNames(
                            {
                              'border-yellow border-b-2':
                                item.anchor === activeAnchorId,
                            },
                            'body-three font-light text-black-100'
                          )}
                          data-testid={`CT-06-Sticky-Navigation-Dropdown-Item-${index}`}
                        >
                          {item.title}
                        </button>
                      )
                    ) : (
                      <Button
                        className="min-h-min"
                        link={{
                          name: item.title,
                          url: `#a-${item.anchor}`,
                          target: '',
                        }}
                        linkCallback={closeMobileNavDropdown}
                        secondary={!item.primary && item.secondary}
                        isLightMode
                        dataTestId={`CT-06-Sticky-Navigation-Dropdown-Button-${index}`}
                      />
                    )}
                  </li>
                ))}

                {displayedCTALinks.length === 2 &&
                  displayedCTALinks.map((ctaLink, index) => (
                    <li key={`${ctaLink.link.name} - ${ctaLink.link.url}`}>
                      <Button
                        className="min-h-min"
                        link={ctaLink.link}
                        linkCallback={closeMobileNavDropdown}
                        isLightMode
                        secondary={ctaLink.secondary}
                        dataTestId={`CT-06-Sticky-Navigation-Dropdown-Displayed-CTA-Link-${index}`}
                      />
                    </li>
                  ))}
              </ul>
            </ShadowContainerVertical>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default StickyNavigation;
